<template>
  <div class="fechamento-dashboard">
    <!-- Row 1 — Informações do Período | Meta de Faturamento | Status (3 cols) -->
    <div class="kpi-grid kpi-grid--3" style="margin-bottom: 1.5rem;">
      <!-- Informações do Período -->
      <box class="summary-card h-100"><div class="info-card">
            <div class="info-row">
              <span class="info-label">{{ t('Período') }}</span>
              <span class="info-value fw-semibold">{{ fechamento?.periodo_formatado }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ t('Data inicial') }}</span>
              <span class="info-value">{{ formatDate(fechamento?.data_inicial) }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ t('Data final') }}</span>
              <span class="info-value">{{ formatDate(fechamento?.data_final) }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ t('Status') }}</span>
              <Tag
                :value="fechamento?.status === 'aberto' ? t('Aberto') : t('Fechado')"
                :severity="fechamento?.status === 'aberto' ? 'success' : 'info'"
              />
            </div>
          </div></box>

      <!-- Meta de Faturamento -->
      <box class="summary-card h-100"><div class="meta-content">
            <div class="summary-label">{{ t('Meta de Faturamento') }}</div>
            <div class="meta-value">
              {{ n(resultadoFinanceiro?.metas?.faturamento?.meta || 0, 'currency') }}
            </div>
            <div class="meta-sub">
              <span :class="getVariationClass(resultadoFinanceiro?.metas?.faturamento?.percentual)">
                {{ resultadoFinanceiro?.metas?.faturamento?.percentual || 0 }}%
              </span>
              <span class="text-muted ms-1">
                {{ n(resultadoFinanceiro?.metas?.faturamento?.realizado || 0, 'currency') }}
              </span>
            </div>
            <KpiProgressBar
              :value="resultadoFinanceiro?.metas?.faturamento?.percentual || 0"
              class="mt-2"
            />
          </div></box>

      <!-- Período Saudável -->
      <box class="summary-card h-100"><div class="status-eficiencia-combined">
            <div class="status-section" :class="periodoStatusClass">
              <div class="status-section-icon">
                <i :class="periodoStatusIcon"></i>
              </div>
              <div class="status-section-body">
                <div class="status-section-title">{{ periodoStatusTitle }}</div>
                <div class="status-section-sub">{{ periodoStatusSub }}</div>
              </div>
            </div>
            <div class="combined-divider"></div>
            <div class="eficiencia-section">
              <div class="eficiencia-header">
                <span class="eficiencia-label">{{ t('Orçamento Consumido') }}</span>
                <span class="eficiencia-value" :class="eficienciaTextClass">
                  {{ eficienciaPercentual }}%
                </span>
              </div>
              <KpiProgressBar :value="eficienciaPercentual" class="mt-1" />
            </div>
          </div></box>
    </div>

    <!-- Row 2 — Metas + Operacional (4 cols) -->
    <div class="kpi-grid kpi-grid--4" style="margin-bottom: 1.5rem;">
      <!-- Meta de Clientes -->
      <box class="summary-card h-100"><div class="d-flex gap-3 align-items-center">
              <div class="summary-icon bg-success">
                <i class="pi pi-users"></i>
              </div>
              <div class="summary-content flex-1">
                <div class="summary-label">{{ t('Meta de Clientes') }}</div>
                <div class="summary-value">{{ resultadoFinanceiro?.metas?.clientes?.meta || 0 }}</div>
                <div class="summary-sub">
                  <span :class="getVariationClass(resultadoFinanceiro?.metas?.clientes?.percentual)">
                    {{ resultadoFinanceiro?.metas?.clientes?.percentual || 0 }}%
                  </span>
                  <span class="text-muted ms-1">
                    {{ resultadoFinanceiro?.metas?.clientes?.realizado || 0 }} {{ t('clientes') }}
                  </span>
                  <TrendIndicator
                    v-if="resultadoFinanceiro?.tendencia?.clientes"
                    :value="resultadoFinanceiro?.tendencia?.clientes"
                  />
                </div>
                <KpiProgressBar
                  :value="resultadoFinanceiro?.metas?.clientes?.percentual || 0"
                  class="mt-2"
                />
              </div>
            </div></box>

      <!-- Meta de Itens -->
      <box class="summary-card h-100"><div class="d-flex gap-3 align-items-center">
              <div class="summary-icon bg-purple">
                <i class="pi pi-box"></i>
              </div>
              <div class="summary-content flex-1">
                <div class="summary-label">{{ t('Meta de itens') }}</div>
                <div class="summary-value" data-testid="fechamento-dashboard-meta-itens">
                  {{ resultadoFinanceiro?.metas?.itens?.meta || 0 }}
                </div>
                <div class="summary-sub">
                  <span :class="getVariationClass(resultadoFinanceiro?.metas?.itens?.percentual)">
                    {{ resultadoFinanceiro?.metas?.itens?.percentual || 0 }}%
                  </span>
                  <span class="text-muted ms-1" data-testid="fechamento-dashboard-itens-realizados">
                    {{ resultadoFinanceiro?.metas?.itens?.realizado || 0 }} {{ t('Itens realizados') }}
                  </span>
                </div>
                <KpiProgressBar
                  :value="resultadoFinanceiro?.metas?.itens?.percentual || 0"
                  class="mt-2"
                />
              </div>
            </div></box>

      <!-- Total Despesas -->
      <box class="summary-card h-100"><div class="d-flex gap-3 align-items-center">
              <div class="summary-icon bg-danger">
                <i class="pi pi-money-bill"></i>
              </div>
              <div class="summary-content flex-1">
                <div class="summary-label">{{ t('Total Despesas') }}</div>
                <div class="summary-value text-danger">
                  {{ n(resultadoFinanceiro?.acumulado?.total_despesas_pagas || 0, 'currency') }}
                </div>
                <div class="summary-sub">
                  <span class="text-muted">{{ t('Previsto no plano') }}:</span>
                  <span class="ms-1">{{ n(resultadoFinanceiro?.plano_contas?.total_previsto || 0, 'currency') }}</span>
                  <TrendIndicator
                    v-if="resultadoFinanceiro?.tendencia?.despesas"
                    :value="resultadoFinanceiro?.tendencia?.despesas"
                  />
                </div>
              </div>
            </div></box>

      <!-- Número de Clientes -->
      <box class="summary-card h-100"><div class="d-flex gap-3 align-items-center">
              <div class="summary-icon bg-warning">
                <i class="pi pi-users"></i>
              </div>
              <div class="summary-content flex-1">
                <div class="summary-label">{{ t('Número de Clientes') }}</div>
                <div class="summary-value">
                  {{ resultadoFinanceiro?.acumulado?.numero_clientes || 0 }}
                </div>
                <div class="summary-sub">
                  <span class="text-muted">{{ t('Clientes atendidos') }}</span>
                </div>
              </div>
            </div></box>

      <!-- Ticket Médio -->
      <box class="summary-card h-100"><div class="d-flex gap-3 align-items-center">
              <div class="summary-icon bg-teal">
                <i class="pi pi-shopping-cart"></i>
              </div>
              <div class="summary-content flex-1">
                <div class="summary-label">{{ t('Ticket Médio') }}</div>
                <div class="summary-value text-teal">
                  {{ n(ticketMedio, 'currency') }}
                </div>
                <div class="summary-sub">
                  <span class="text-muted">{{ t('Por cliente') }}</span>
                  <TrendIndicator
                    v-if="resultadoFinanceiro?.tendencia?.ticket_medio"
                    :value="resultadoFinanceiro?.tendencia?.ticket_medio"
                  />
                </div>
              </div>
            </div></box>

      <!-- Lucro Contábil -->
      <box class="summary-card h-100"><div class="d-flex gap-3 align-items-center">
                <div class="summary-icon" :class="(resultadoFinanceiro?.lucro_mensal ?? 0) >= 0 ? 'bg-success' : 'bg-danger'">
                  <i :class="(resultadoFinanceiro?.lucro_mensal ?? 0) >= 0 ? 'pi pi-arrow-up' : 'pi pi-arrow-down'"></i>
                </div>
                <div class="summary-content flex-1">
                  <div class="summary-label">{{ t('Lucro Contábil') }}</div>
                  <div class="summary-value" :class="(resultadoFinanceiro?.lucro_mensal ?? 0) >= 0 ? 'text-success' : 'text-danger'">
                    {{ n(resultadoFinanceiro?.lucro_mensal || 0, 'currency') }}
                  </div>
                  <div class="summary-sub">
                    <span class="text-muted">{{ resultadoFinanceiro?.margem_lucro || 0 }}% {{ t('margem') }}</span>
                    <TrendIndicator
                      v-if="resultadoFinanceiro?.tendencia?.lucro"
                      :value="resultadoFinanceiro?.tendencia?.lucro"
                    />
                  </div>
                  <KpiProgressBar
                    :value="resultadoFinanceiro?.margem_lucro || 0"
                    :success-threshold="30"
                    :warning-threshold="10"
                    class="mt-2"
                  />
                </div>
              </div></box>

      <!-- Pago este mês -->
      <box class="summary-card h-100"><div class="d-flex gap-3 align-items-center">
                <div class="summary-icon bg-info">
                  <i class="pi pi-wallet"></i>
                </div>
                <div class="summary-content flex-1">
                  <div class="summary-label">{{ t('Pago este mês') }}</div>
                  <div class="summary-value">
                    {{ n(resultadoFinanceiro?.acumulado?.total_recebimentos || 0, 'currency') }}
                  </div>
                  <div class="summary-sub">
                    <span class="text-muted">{{ t('Dinheiro') }}:</span>
                    <span class="ms-1">{{ n(resultadoFinanceiro?.acumulado?.total_recebido_dinheiro || 0, 'currency') }}</span>
                  </div>
                </div>
              </div></box>

      <!-- Caixa Mensal -->
      <box class="summary-card h-100"><div class="d-flex gap-3 align-items-center">
                  <div class="summary-icon" :class="(resultadoFinanceiro?.caixa_mensal ?? 0) >= 0 ? 'bg-success' : 'bg-danger'">
                    <i :class="(resultadoFinanceiro?.caixa_mensal ?? 0) >= 0 ? 'pi pi-arrow-up' : 'pi pi-arrow-down'"></i>
                  </div>
                  <div class="summary-content flex-1">
                    <div class="summary-label">{{ t('Caixa Mensal') }}</div>
                    <div class="summary-value" :class="(resultadoFinanceiro?.caixa_mensal ?? 0) >= 0 ? 'text-success' : 'text-danger'">
                      {{ n(resultadoFinanceiro?.caixa_mensal || 0, 'currency') }}
                    </div>
                    <div class="summary-sub">
                      <span class="text-muted">{{ t('Recebimentos disponíveis - despesas') }}</span>
                    </div>
                  </div>
                </div></box>

      <!-- Recebido este mês -->
      <box class="summary-card h-100"><div class="d-flex gap-3 align-items-center">
                  <div class="summary-icon bg-primary">
                    <i class="pi pi-check-circle"></i>
                  </div>
                  <div class="summary-content flex-1">
                    <div class="summary-label">{{ t('Recebido este mês') }}</div>
                    <div class="summary-value">
                      {{ n(resultadoFinanceiro?.acumulado?.total_recebimentos_disponiveis || 0, 'currency') }}
                    </div>
                    <div class="summary-sub">
                      <span class="text-muted">{{ t('Disponível em conta') }}</span>
                    </div>
                  </div>
                </div></box>
    </div>

    <!-- Atualizar -->
    <div class="d-flex justify-content-end">
      <Button data-testid="fechamentos-dashboard-refresh-button"
        :label="t('Atualizar')"
        icon="pi pi-refresh"
        :loading="isLoading"
        @click="emit('refresh')"
        severity="secondary"
        outlined
        size="small"
        class="btn-atualizar"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, defineComponent, h } from 'vue';
import { useI18n } from 'vue-i18n';
import KpiProgressBar from '../../ui/KpiProgressBar.vue';

interface TrendValue {
  percentual: number;
  anterior?: number;
  atual?: number;
}

interface FechamentoResumo {
  variacao_percentual?: number | string | null;
  total_efetivo?: number | string | null;
  total_previsto?: number | string | null;
  total_despesas?: number | string | null;
  lucro_liquido?: number | string | null;
}

interface FechamentoData {
  id?: number | string;
  status?: string;
  data_inicial?: string;
  data_final?: string;
  periodo_formatado?: string;
  meta_faturamento?: number | null;
  resumo?: FechamentoResumo | null;
  [key: string]: unknown;
}

interface ResultadoFinanceiroMeta {
  meta?: number | null;
  realizado?: number | null;
  percentual?: number | null;
}

interface ResultadoFinanceiroData {
  metas?: {
    faturamento?: ResultadoFinanceiroMeta;
    clientes?: ResultadoFinanceiroMeta;
    itens?: ResultadoFinanceiroMeta;
  };
  plano_contas?: {
    total_previsto?: number | null;
  };
  acumulado?: {
    total_despesas_pagas?: number | null;
    numero_clientes?: number | null;
    total_recebimentos?: number | null;
    total_recebido_dinheiro?: number | null;
    total_recebimentos_disponiveis?: number | null;
  };
  ticket_medio?: number | null;
  lucro_mensal?: number | null;
  margem_lucro?: number | null;
  caixa_mensal?: number | null;
  tendencia?: {
    faturamento?: TrendValue | null;
    clientes?: TrendValue | null;
    lucro?: TrendValue | null;
    despesas?: TrendValue | null;
    ticket_medio?: TrendValue | null;
  } | null;
  [key: string]: unknown;
}

const { t, n } = useI18n();

// Trend Indicator Component (inline)
const TrendIndicator = defineComponent({
  name: 'TrendIndicator',
  props: {
    value: {
      type: Object as () => TrendValue | null,
      default: null
    }
  },
  setup(props) {
    const trendClass = computed<string>(() => {
      if (!props.value) return '';
      const percentual = props.value.percentual;
      if (percentual > 0) return 'trend-up badge-success';
      if (percentual < 0) return 'trend-down badge-danger';
      return 'trend-neutral badge-secondary';
    });
    const trendIcon = computed<string>(() => {
      if (!props.value) return '';
      const percentual = props.value.percentual;
      if (percentual > 0) return 'pi pi-arrow-up';
      if (percentual < 0) return 'pi pi-arrow-down';
      return 'pi pi-minus';
    });
    const trendText = computed<string>(() => {
      if (!props.value) return '';
      const percentual = Math.abs(props.value.percentual);
      const signal = props.value.percentual > 0 ? '+' : '';
      return `${signal}${percentual}%`;
    });
    return () => props.value
      ? h('span', { class: ['trend-badge', 'ms-1', trendClass.value] }, [
          h('i', { class: trendIcon.value }),
          h('span', { class: 'ms-1' }, trendText.value)
        ])
      : null;
  }
});

const props = defineProps<{
  fechamento: FechamentoData;
  resultadoFinanceiro: ResultadoFinanceiroData | null;
  isLoading?: boolean;
}>();

const emit = defineEmits<{
  (e: 'refresh'): void;
}>();

const isExporting = ref<boolean>(false);

// Period status computeds
const periodoStatusClass = computed<string>(() => {
  const v = Number(props.fechamento?.resumo?.variacao_percentual) || 0;
  if (v <= 0) return 'status-section--success';
  if (v <= 20) return 'status-section--warning';
  return 'status-section--danger';
});

const periodoStatusIcon = computed<string>(() => {
  const cls = periodoStatusClass.value;
  if (cls === 'status-section--success') return 'pi pi-check-circle';
  if (cls === 'status-section--warning') return 'pi pi-exclamation-triangle';
  return 'pi pi-times-circle';
});

const periodoStatusTitle = computed<string>(() => {
  const cls = periodoStatusClass.value;
  if (cls === 'status-section--success') return t('Período Saudável');
  if (cls === 'status-section--warning') return t('Em Atenção');
  return t('Período Crítico');
});

const periodoStatusSub = computed<string>(() => {
  const v = Number(props.fechamento?.resumo?.variacao_percentual) || 0;
  const signal = v >= 0 ? '+' : '';
  return `${signal}${v}% ${t('vs previsto')}`;
});

const eficienciaTextClass = computed<string>(() => {
  const ef = eficienciaPercentual.value;
  if (ef <= 100) return 'text-success';
  if (ef <= 120) return 'text-warning';
  return 'text-danger';
});

const formatDate = (date?: string | null): string => {
  if (!date) return '-';
  const d = new Date(date + 'T00:00:00');
  return d.toLocaleDateString('pt-BR');
};

// Computed values for indicators
const ticketMedio = computed<number>(() => {
  return props.resultadoFinanceiro?.ticket_medio || 0;
});

const eficienciaPercentual = computed<number>(() => {
  const despesas = props.resultadoFinanceiro?.acumulado?.total_despesas_pagas || 0;
  const previsto = props.resultadoFinanceiro?.plano_contas?.total_previsto || 0;
  if (previsto === 0) return 0;
  return Math.round((despesas / previsto) * 100);
});

const getVariationClass = (percentual?: number | null): string => {
  if (!percentual) return 'text-muted';
  if (percentual >= 100) return 'text-success';
  if (percentual >= 80) return 'text-warning';
  return 'text-danger';
};
</script>

<style scoped>
.fechamento-dashboard {
  padding: 0;
}

.btn-atualizar :deep(.p-button-label),
.btn-atualizar :deep(.p-button-icon) {
  color: #f59e0b !important;
}

.btn-atualizar :deep(.p-button-outlined) {
  border-color: #f59e0b !important;
}

.btn-atualizar {
  border-color: #f59e0b !important;
  color: #f59e0b !important;
  background: white !important;
}

/* KPI Grid Layout */
.kpi-grid {
  display: grid;
  gap: 1rem;
}

.kpi-grid--5 {
  grid-template-columns: repeat(5, 1fr);
}

.kpi-grid--4 {
  grid-template-columns: repeat(4, 1fr);
}

.kpi-grid--3 {
  grid-template-columns: repeat(3, 1fr);
}

.kpi-grid--2 {
  grid-template-columns: repeat(2, 1fr);
}

.kpi-grid--1 {
  grid-template-columns: 1fr;
}

@media (max-width: 1100px) {
  .kpi-grid--5 { grid-template-columns: repeat(3, 1fr); }
  .kpi-grid--4 { grid-template-columns: repeat(2, 1fr); }
  .kpi-grid--3 { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 900px) {
  .kpi-grid--5 { grid-template-columns: repeat(2, 1fr); }
  .kpi-grid--4 { grid-template-columns: repeat(2, 1fr); }
  .kpi-grid--3 { grid-template-columns: 1fr; }
}

@media (max-width: 500px) {
  .kpi-grid--5,
  .kpi-grid--4,
  .kpi-grid--3 { grid-template-columns: 1fr; }
}


/* Summary Cards */
.summary-card :deep(.p-card-body) {
  padding: 1rem;
}


.summary-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.summary-icon i {
  font-size: 1.25rem;
  color: white;
}

.summary-content {
  flex: 1;
  min-width: 0;
}

.summary-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

.summary-value {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 0.25rem;
  color: #111827;
}

.summary-sub {
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-wrap: wrap;
}

/* Colors */
.bg-primary { background-color: #3b82f6 !important; }
.bg-success { background-color: #10b981 !important; }
.bg-danger { background-color: #ef4444 !important; }
.bg-warning { background-color: #f59e0b !important; }
.bg-info { background-color: #06b6d4 !important; }
.bg-secondary { background-color: #64748b !important; }
.bg-purple { background-color: #8b5cf6 !important; }
.bg-teal { background-color: #14b8a6 !important; }
.bg-orange { background-color: #f97316 !important; }

.text-success { color: #10b981 !important; }
.text-danger { color: #ef4444 !important; }
.text-warning { color: #f59e0b !important; }
.text-primary { color: #3b82f6 !important; }
.text-info { color: #06b6d4 !important; }
.text-secondary { color: #64748b !important; }
.text-purple { color: #8b5cf6 !important; }
.text-teal { color: #14b8a6 !important; }
.text-orange { color: #f97316 !important; }

/* Trend Badge */
.trend-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.7rem;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-weight: 600;
}

.trend-up {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.trend-down {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.trend-neutral {
  background: rgba(100, 116, 139, 0.1);
  color: #64748b;
}

/* Responsive */
@media (max-width: 768px) {
  .summary-value {
    font-size: 1.125rem;
  }
}

/* Info Card (Informações do Período) */
.info-card {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.375rem 0;
  border-bottom: 1px solid #e5e7eb;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  color: #6b7280;
  font-size: 0.875rem;
}

.info-value {
  font-weight: 500;
  font-size: 0.875rem;
}

/* Meta Card */
.meta-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
}

.meta-value {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 0.25rem;
  color: #111827;
}

.meta-sub {
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
}

/* Status + Eficiência Card */
.status-eficiencia-combined {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 0;
}

.status-section {
  border-radius: 8px;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.status-section--success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.status-section--warning {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
}

.status-section--danger {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
}

.status-section-icon {
  flex-shrink: 0;
}

.status-section-icon i {
  font-size: 1.5rem;
  opacity: 0.9;
}

.status-section-body {
  flex: 1;
}

.status-section-title {
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.2;
}

.status-section-sub {
  font-size: 0.75rem;
  opacity: 0.85;
}

.combined-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 0.5rem 0;
}

.eficiencia-section {
  padding: 0.5rem 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.eficiencia-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
}

.eficiencia-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  color: #6b7280;
}

.eficiencia-value {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.2;
}
</style>
